import React, { useState, useEffect, useCallback } from "react";
import CommonSelect from "../../../../core/common/commonSelect";
import api from "../../../../services/api";
import Loader from "../../../../core/common/loader";
import { notify } from "../../../../services/toast.config";

// Types
interface AddCategoryProps {
  formDataCompanyChoose: {
    company_id: string;
    company_name: string;
    company_type: string;
  };
  updateUI: () => Promise<void>;
}

type CategoryFormData = {
  name: string | any;
  status: string | any;
  company_id: string | any;
  company_type: string | any;
  company_name: string | any;
};

const DEFAULT_FORM_DATA: CategoryFormData = {
  name: null,
  status: null,
  company_id: null,
  company_type: null,
  company_name: null,
};

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const AddCategory: React.FC<AddCategoryProps> = ({
  formDataCompanyChoose,
  updateUI,
}) => {
  // 🔹 Local Storage
  const signature = localStorage.getItem("signature");

  // 🔹 State
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState<CategoryFormData>(DEFAULT_FORM_DATA);

  // 🔹 Helper Functions
  const resetForm = useCallback(() => {
    const { company_id, company_name, company_type } = formDataCompanyChoose;
    setFormData({
      name: "",
      status: undefined,
      company_id,
      company_name,
      company_type,
    });
    setInvalidFields(new Set());
  }, [formDataCompanyChoose]);

  const validateForm = useCallback((): boolean => {
    const requiredFields = [
      { key: "name", label: "Category Name" },
      { key: "status", label: "Status" },
      { key: "company_id", label: "Company" },
    ];

    const invalid = new Set<string>();
    requiredFields.forEach(({ key }) => {
      if (!formData[key as keyof CategoryFormData]?.toString().trim()) {
        invalid.add(key);
      }
    });

    setInvalidFields(invalid);
    return invalid.size === 0;
  }, [formData]);

  const clearFieldError = useCallback((fieldName: string) => {
    setInvalidFields((prev) => {
      const newSet = new Set(prev);
      newSet.delete(fieldName);
      return newSet;
    });
  }, []);

  // 🔹 Event Handlers
  const handleInputChange = useCallback(
    (key: string, value: string) => {
      clearFieldError(key);
      setFormData((prev) => ({ ...prev, [key]: value }));
    },
    [clearFieldError]
  );

  const handleSubmitCategory = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setIsLoading(true);

      const dataToSend = {
        signature,
        name: formData.name.trim(),
        status: formData.status.toLowerCase() === "active" ? 1 : 0,
        company_id: formData.company_id,
        company_type: formData.company_type,
      };

      const response = await api.post("userService/addCategory", dataToSend);

      resetForm();
      notify.success(
        response.data?.message[0]?.msg || "Category added successfully."
      );
      // Close modal
      const dismissButton = document.querySelector(
        "#add-category-dismiss-modal-btn"
      ) as HTMLButtonElement;
      if (dismissButton) {
        dismissButton.click();
      }
      await updateUI();
    } catch (error: any) {
      const errorMessage =
        error?.data?.message?.[0]?.msg ||
        error?.data?.message ||
        "Internal Server Error!";
      notify.error(errorMessage);
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  // Initialize form data
  useEffect(() => {
    resetForm();
  }, [resetForm]);

  return (
    <>
      {isLoading && <Loader />}

      <div className="modal fade" id="add_category">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Category</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                disabled={isSubmitting}>
                <i className="ti ti-x" />
              </button>
            </div>

            <form onSubmit={handleSubmitCategory}>
              <div className="modal-body pb-0">
                <div className="row">
                  {/* Company Selection */}
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Company <span className="text-danger">*</span>
                    </label>
                    <div
                      className={
                        invalidFields.has("company_id")
                          ? "border border-danger rounded"
                          : ""
                      }>
                      <CommonSelect
                        className="select"
                        options={[]}
                        defaultValue={{
                          value: formData.company_id,
                          label: `${formData.company_name} - ${formData.company_type}`,
                        }}
                        isDisabled={true}
                      />
                    </div>
                    {invalidFields.has("company_id") && (
                      <div className="invalid-feedback d-block">
                        <small className="text-danger">
                          Company is required
                        </small>
                      </div>
                    )}
                  </div>

                  {/* Category Name */}
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Category Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${
                        invalidFields.has("name")
                          ? "is-invalid border-danger"
                          : ""
                      }`}
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      placeholder="Enter category name"
                      disabled={isSubmitting}
                    />
                    {invalidFields.has("name") && (
                      <div className="invalid-feedback d-block">
                        <small className="text-danger">
                          Category Name is required
                        </small>
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Status <span className="text-danger">*</span>
                    </label>
                    <div
                      className={
                        invalidFields.has("status")
                          ? "border border-danger rounded"
                          : ""
                      }>
                      <CommonSelect
                        key={`status-${formData.company_id}-${Date.now()}`}
                        options={STATUS_OPTIONS}
                        defaultValue={
                          formData.status
                            ? { value: formData.status, label: formData.status }
                            : undefined
                        }
                        onChange={(selected: any) =>
                          handleInputChange("status", selected?.value || "")
                        }
                        placeHolder="Select Status"
                        isDisabled={isSubmitting}
                      />
                    </div>
                    {invalidFields.has("status") && (
                      <div className="invalid-feedback d-block">
                        <small className="text-danger">
                          Status is required
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  disabled={isSubmitting}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Adding...
                    </>
                  ) : (
                    "Add Category"
                  )}
                </button>

                {/* Button for closing the modal */}
                <button
                  id="add-category-dismiss-modal-btn"
                  type="button"
                  className="d-none"
                  data-bs-dismiss="modal"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddCategory;
